import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | error | expired
  const pollCount = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    let active = true;
    const poll = async () => {
      if (!active) return;
      if (pollCount.current >= 10) { setStatus("expired"); return; }
      pollCount.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("error");
          return;
        }
      } catch {
        setStatus("error");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [refreshUser]);

  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center">
      {status === "checking" && (
        <>
          <Loader2 className="w-12 h-12 mx-auto text-[#2C4A3E] animate-spin" />
          <h1 className="mt-6 font-serif text-3xl text-[#1A2621]">Confirming your enrollment…</h1>
          <p className="mt-2 text-[#5A6B61]">Just a moment while we verify your payment.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-[#3D7A5A]" />
          <h1 className="mt-6 font-serif text-3xl sm:text-4xl text-[#1A2621]">You're enrolled!</h1>
          <p className="mt-2 text-[#5A6B61]">Welcome to the course. All 90 chapters are now unlocked.</p>
          <button data-testid="go-dashboard-btn" onClick={() => navigate("/dashboard")} className="mt-8 px-8 py-4 rounded-full bg-[#2C4A3E] text-[#FAF8F5] font-semibold hover:bg-[#223B31]">
            Start learning
          </button>
        </>
      )}
      {(status === "error" || status === "expired") && (
        <>
          <XCircle className="w-14 h-14 mx-auto text-[#C47A53]" />
          <h1 className="mt-6 font-serif text-3xl text-[#1A2621]">We couldn't confirm your payment</h1>
          <p className="mt-2 text-[#5A6B61]">
            {status === "expired" ? "Verification is taking longer than expected." : "Something went wrong."} If you were charged, your access will update shortly.
          </p>
          <button onClick={() => navigate("/dashboard")} className="mt-8 px-8 py-4 rounded-full bg-[#2C4A3E] text-[#FAF8F5] font-semibold">Go to dashboard</button>
        </>
      )}
    </div>
  );
}
